import { BehaviorSubject, Subject } from 'rxjs';

import { deepEqual } from '../_helpers/equaleCompare';
import { roundMoney } from '../_helpers/money';
import { roundTo } from '../_helpers/decimal';
import { Waiter } from './user';
import { CustomerService } from '../_services/customer.service';
import { ProductPrice } from './product';
import { Client } from './client';

export enum OrderStatus {
  New = 1,
  Accepted = 3,
  InProduction = 4,
  Ready = 5,
  Dispached = 6,
  Delivered = 7,
  Rejected = 8,
  Canceled = 9,
  Completed = 10,
  Invalid = 11,
  NewPreorder = 12,
  Pending = 13,
  PendingPayment = 14
}

export enum OrderStatusLog {
  New = 1,
  Accepted = 3,
  InProduction = 4,
  Ready = 5,
  Dispached = 6,
  Delivered = 7,
  Rejected = 8,
  Canceled = 9,
  Completed = 10,
  Invalid = 11,
  NewPreorder = 12,
  Pending = 13,
  PendingPayment = 14,
  Paid = 101,
  NotSentToPOS = 102,
  OrderItemStatusChanged = 103,
}

export enum OrderItemStatus {
  Default = 0,
  Ready = 1,
  Delivered = 2,
  InProduction = 3
}

export enum DeliveryType {
  InHouse = "InHouse", // served at a table
  Pickup = "Pickup",
  Delivery = "Delivery",
  EatIn = "EatIn" // served on a plate
}

export interface UserBase {
  userID: number;
  firstName: string;
  lastName: string;
  eMail: string;
}

export interface CreatedBy extends UserBase {
  verified: boolean;
}

export interface Customer extends CustomerRating {
  customerID: number;
  userID?: number;

  name: string;
  address: string;
  phoneNumber: string;
  note: string;
  isBlocked: boolean;

  discount: number;
  /** True = popust primenjuje POS na naplati (kupac došao preko POS-a) → prikaži ali NE obračunavaj u total. */
  discountByPos?: boolean;
  priceListName: string;
}

/** Izvor adrese u spojenoj listi (mora da prati backend Organization.CustomerAddressSource). */
export enum CustomerAddressSource {
  User = 0,          // account-global (UserAddress) — read-only za osoblje
  Organization = 1,  // org-managed (CustomerAddress) — osoblje dodaje/briše
  Company = 2,       // legacy Customers.Address (ne-org firme) — editabilno
}

/** Unifikovana adresa dostave kupca za konobar formu (spaja org + account + legacy). */
export interface CustomerAddress {
  customerAddressID: number;
  label: string;
  /** Ime primaoca kome se šalje (ime i prezime korisnika ili naziv firme). */
  name: string;
  address: string;
  latitude?: number;
  longitude?: number;
  note: string;
  source: CustomerAddressSource;
  editable: boolean;
}

export interface CustomerRating {
  orderCount: number;
  plus: number;
  minus: number;
  overallPlus: number;
  overallMinus: number;
}
export interface CompanyBasicInfo {
  companyID: number;
  name: string;
}

export interface InvalidOrderDetails {
  items?: InvalidOrderItem[],
  total: number
}

export interface InvalidOrderItem {
  name: string;
  price: number;
  quantity: number;
  modifiers?: InvalidOrderItem[];
}

export enum PaymentMethod {
  Card = 0,
  Cash
}

export class Order {
  companyID = 0;
  orderID = 0;
  created!: Date;
  deliveryType!: DeliveryType;
  status!: OrderStatus;
  tableID?: number;
  items: OrderItem[] = [];
  itemsChanged = new BehaviorSubject<OrderItem[]>([]);
  dueDate?: string | Date;
  modified?: string | Date;
  accepted?: string | Date;
  deliveryDetail?: OrderDeliveryDetail;
  createdBy?: CreatedBy;
  waiter?: Waiter;
  note: string = '';
  orderNo: string = '';
  group: string = '';
  /** Anonimni token za javno praćenje statusa (kiosk QR) — server ga vraća u create odgovoru.
   *  URL /track strane sastavlja klijent iz tokena (TrackingUrlService) — ne prenosi se kroz API. */
  trackingToken?: string;
  rejectReason = '';
  provider: 'wolt' | 'mrd' | '' = '';
  isPaid: boolean = false;
  private _customer?: Customer;
  originCompany?: CompanyBasicInfo;
  client?: Client;
  invalidOrderDetails?: InvalidOrderDetails;
  paymentMethod?: PaymentMethod;
  coupons: number[] = [];
  organizationCustomerID?: number;
  discountRate?: number;
  discountAmount?: number;
  /** Cena dostave upisana u zasebnu kolonu (region-based, kada nije dodata kao stavka).
   *  Kada je dostava naplaćena kao stavka porudžbine, ovo je null (backend garantuje da se ne računa dvaput). */
  deliveryFee?: number;

  customerProductPrices = new BehaviorSubject<ProductPrice[]>([]);

  get customer() {
    return this._customer;
  }
  set customer(val: Customer | undefined) {
    this._customer = val;
    if (val && !this.orderID && val.userID) {
      var cs = globalThis.AppInjector.get(CustomerService);

      cs.getProductPrices(this.companyID, val.userID!).subscribe({
        next: prices => {
          this.customerProductPrices.next(prices);
        }
      });
    }
  }

  get total(): number {
    var total = this.items.filter(i => !i.isCanceled).reduce((s, i) => {
      return s + i.total;
    }, 0);

    return total + (this.deliveryFee ?? 0);
  }

  /** Popust porudžbine u novcu (lični popust kupca ili kupon: `discountRate` / `discountAmount`).
   *  Isto pravilo kao na serveru (DiscountAllocator → Order.NetTotal): računa se na zbir aktivnih
   *  STAVKI (dostava se ne umanjuje), popusti se ne sabiraju nego važi veći, najviše do zbira
   *  stavki, na 2 decimale. Računa se iz stavki (a ne iz serverskog `netTotal`) da prati i
   *  stavke stornirane na uređaju pre slanja. */
  get discountValue(): number {
    const rate = Math.min(Math.max(Number(this.discountRate) || 0, 0), 1);
    const amount = Math.max(Number(this.discountAmount) || 0, 0);
    const subtotal = this.total - (this.deliveryFee ?? 0);
    if ((rate === 0 && amount === 0) || subtotal <= 0) {
      return 0;
    }

    return Math.min(roundMoney(Math.min(Math.max(subtotal * rate, amount), subtotal)), roundMoney(subtotal));
  }

  /** Iznos za naplatu: stavke − popust porudžbine + dostava (server isti iznos čuva kao
   *  NetTotal i po njemu proverava naplatu). Bez popusta je isto što i `total`. */
  get totalWithDiscount(): number {
    const discount = this.discountValue;
    return discount > 0 ? roundMoney(this.total - discount) : this.total;
  }

  constructor(json?: any) {
    if (json) {
      Object.assign(this, json);
    }

    this.created = new Date(this.created);

    this.items.forEach((item, index) => {
      this.items[index] = OrderItem.fromJSON(item);
    });

    this.deliveryType = <any>DeliveryType[this.deliveryType];
  }


  replaceOrderItem(old: OrderItem, newItem: OrderItem) {
    var index = this.items.indexOf(old);
    if (index != -1) {
      this.items[index] = newItem;

      this.itemsChanged.next(this.items);
    }
  }

  /**
   * Add an item, merging with an identical existing one (same product/modifiers/note).
   * When `moveToBottom` is true (waiter order list), a merged item is moved to the
   * end so the most recently added item is always at the bottom of the list.
   */
  addItem(item: OrderItem, moveToBottom: boolean = false): void {
    item.order = this;

    let found: OrderItem | undefined;
    for (const i of this.items) {
      const tmp1 = {} as OrderItem;
      Object.assign(tmp1, i);
      tmp1.quantity = 0;

      const tmp2 = {} as OrderItem;
      Object.assign(tmp2, item);
      tmp2.quantity = 0;

      if (deepEqual(tmp1, tmp2)) {
        found = i;
        break;
      }
    }

    if (found) {
      // decimalna količina (npr. 0,1 + 0,2) — bez float šuma
      found.quantity = roundTo(found.quantity + item.quantity, 3);
      this.items = moveToBottom
        ? [...this.items.filter(i => i !== found), found]   // most-recent at the bottom
        : [...this.items];
    } else {
      this.items = [...this.items, item];
    }

    this.itemsChanged.next(this.items);
  }

  removeItem(item: OrderItem): void {
    var index = this.items.indexOf(item);

    this.items.splice(index, 1);
    this.itemsChanged.next(this.items);
  }

  public toJSON() {
    const { customerProductPrices, itemsChanged, ...otherProps } = this;

    var obj = Object.entries(otherProps) // Create array from object
      .map(([key, value]) => [  // change key to remove '_'
        key.startsWith('_') ? key.substring(1) : key,
        value
      ])
      .reduce((acc, [key, value]) => { // Transform back to object
        acc[key] = value;
        return acc;
      }, {} as any);

    return obj;

  }
}

export class OrderItemModifier {
  modifierID: number | null = null;
  productID = 0;
  quantity = 0;
  price = 0;
  get total() {
    return this.quantity * this.price;
  }

  static fromJSON(json: any): OrderItemModifier {
    const i = new OrderItemModifier();
    Object.assign(i, json);
    return i;
  }
}

export interface OrderItemToCancel {
  orderItemID: number;
  orderItemReferenceID: string;
  orderID: number;
}

export class OrderItem {
  orderItemID: number = 0;
  modifiers: OrderItemModifier[] = [];
  quantity: number = 0;
  productID: number = 0;
  referenceID: string = '';
  name: string = '';
  note: string = '';
  price: number = 0;
  status: OrderItemStatus = OrderItemStatus.Default;
  isCanceled = false;
  orderItemReferenceID = '';
  invalidOrderDetails?: InvalidOrderItem[];
  order?: Order;

  get total() {
    var total = this.quantity * (this.price + this.modifierTotal());
    // Popust se obračunava klijentski SAMO kad ga eMeni primenjuje. Ako je discountByPos, POS ga primeni na
    // naplati (precheck vrati umanjen iznos), pa ga ovde samo prikazujemo, ne oduzimamo. Vidi org customer migraciju.
    if (this.order?.customer?.discount && !this.order.customer.discountByPos) {
      total = total - (total * this.order.customer.discount / 100);
    }
    return total;
  }

  private modifierTotal(): number {
    return this.modifiers?.reduce((s, i) => {
      return s + i.total;
    }, 0) || 0;
  }

  static fromJSON(json: any): OrderItem {
    const oi = new OrderItem();
    Object.assign(oi, json);

    oi.modifiers = oi.modifiers || [];

    oi.modifiers?.forEach((m, i) => {
      oi.modifiers[i] = OrderItemModifier.fromJSON(m);
    })
    return oi;
  }


  public toJSON() {
    const { order, ...otherProps } = this;

    var obj = Object.entries(otherProps) // Create array from object
      .map(([key, value]) => [  // change key to remove '_'
        key.startsWith('_') ? key.substring(1) : key,
        value
      ])
      .reduce((acc, [key, value]) => { // Transform back to object
        acc[key] = value;
        return acc;
      }, {} as any);

    return obj;

  }
}

export interface OrderDeliveryDetail {
  name: string;
  phoneNumber: string;
  address: string;
  note: string;
  provider?: any;
  longitude?: number;
  latitude?: number;
}
export interface CancelOrderItem {
  tableID: number;
  orderItemID: string;
}

export interface SearchOrderOptions {
  companyID: number;
  createdFrom?: string | null | Date;
  createdTo?: string | null | Date;
  modifiedFrom?: string | null | Date;
  modifiedTo?: string | null | Date;
  fromOrderID?: number | null;
  status?: OrderStatus[];
  deliveryType?: DeliveryType[] | null;
  orderNo?: string | null;
  invalid?: boolean | null;
  provider?: string | null;
  dueDateFrom?: string | null | Date;
  dueDateTo?: string | null | Date;
  /** Filtrira po zaduženom zaposlenom (Order.WaiterId) — delivery app dobija samo svoje porudžbine. */
  waiterId?: number | null;
  /** Straničenje (`order/get`): preskoči `offset` redova pa vrati najviše `limit`.
      Stariji backend ih ignoriše — potrošač to prepoznaje po izostanku `total`-a u odgovoru. */
  offset?: number | null;
  limit?: number | null;
}

export interface CancelOrderItemResponse {
  success: boolean;
  error: string;
}

export interface OrderStatusChangeItem {
  orderID: number;
  status: OrderStatusLog;
  date: string;
  changedBy: string;
  orderItemID?: number;
}

export interface PlacedOrder {
  created: string;
  status: OrderStatus;
  items: OrderItem[];
  orderNo: string;
  orderID: number;
}
